import { ZfDivider, ZfIcon, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router';
import { useClient, useTools } from '../../../api';
import type { AdvisoryNeedTaskDto } from '../../../api/generated/client';
import { useNeedContentContext } from '../../../contexts/need-content-context';
import { useElementHeight } from '../../../hooks/useElementHeight';
import { buildAdvisoryNeedTask } from '../../../utils/eventUtils';
import { hasEntry } from '../../../utils/utils';
import { Document } from '../components/Document';
import { ToolLink } from '../components/ToolLink';
import { NeedEventModal } from '../modals/NeedEventModal';

interface IProps {
    containerDimensionsHeight: number;
    hideBackButton?: boolean;
}

export const NeedDetailPanel: FunctionComponent<IProps> = ({ containerDimensionsHeight, hideBackButton }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { data: activeClient } = useClient();
    const { data: tools } = useTools();
    const { activeAdvisoryNeed, activeTalkingPoint, activeTopic, setActiveAdvisoryNeed } = useNeedContentContext();

    const titleRef = useRef<HTMLDivElement>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const titleHeight = useElementHeight(titleRef, [activeAdvisoryNeed?.name, activeAdvisoryNeed?.description, i18n.language]);

    const newTask: AdvisoryNeedTaskDto = buildAdvisoryNeedTask(i18n.language, activeTopic, activeTalkingPoint, activeAdvisoryNeed, activeClient);

    if (!activeAdvisoryNeed || !tools) {
        return null;
    }

    const needTools = activeAdvisoryNeed.toolIds.map((id: number) => tools.find((tool) => tool.id === id));
    const contentHeight = `${containerDimensionsHeight - titleHeight}px`;

    const onEditClick = () => setIsModalOpen(true);
    const onCloseClick = () => setIsModalOpen(false);

    const onBackClick = () => {
        setActiveAdvisoryNeed(undefined);
    };

    const handleGoToClientRoadmapClick = () => {
        navigate('../../roadmap');
    };

    let topSectionTitle = activeAdvisoryNeed.name[i18n.language];
    if (topSectionTitle.includes(':')) {
        topSectionTitle = topSectionTitle.split(':')[0];
    }

    return (
        <div className="topic-screen__panel_3">
            <div className="topic-screen__panel_3__header" ref={titleRef}>
                <div className="topic-screen__panel_3__header-left">
                    {!hideBackButton ? (
                        <div className="topic-screen__panel_3__header-button" onClick={onBackClick}>
                            <ZfIcon name="chevron_left" />
                            <h5>{topSectionTitle}</h5>
                        </div>
                    ) : (
                        <div className="topic-screen__panel_3__header-button" style={{ cursor: 'auto' }}>
                            <h5>{topSectionTitle}</h5>
                        </div>
                    )}
                    <span>{activeAdvisoryNeed.description[i18n.language]}</span>
                </div>
                {!!activeClient && (
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', justifyContent: 'end' }}>
                        <ZfTextButton size="medium" onClick={onEditClick} hierarchy={'tertiary'}>
                            {t('eventModalAddToRoadmapButton')}
                        </ZfTextButton>
                        <ZfTextButton size="medium" onClick={handleGoToClientRoadmapClick} hierarchy={'primary'}>
                            {t('viewClientRoadmap')}
                        </ZfTextButton>
                    </div>
                )}
            </div>
            <div className="topic-screen__panel_3__body" style={{ height: contentHeight, maxHeight: contentHeight, overflowY: 'auto' }}>
                <div className="topic-screen__panel_3__description">
                    <ZfTypography>
                        <ReactMarkdown>{activeAdvisoryNeed.proposedActions[i18n.language]}</ReactMarkdown>
                    </ZfTypography>
                    {isModalOpen && <NeedEventModal isOpen={isModalOpen} onClose={onCloseClick} event={newTask} />}
                </div>
                <ZfDivider />
                <ZfStack direction="column" spacing="extra-extra-large" style={{ padding: 'var(--padding-vertical-xxl) var(--padding-horizontal-xl)' }}>
                    {hasEntry(activeAdvisoryNeed.checkListIds) && (
                        <ZfStack direction="column" spacing="medium">
                            <h5>{t('titleChecklist')}</h5>
                            <div>
                                {activeAdvisoryNeed.checkListIds.map((id, index) => (
                                    <Document key={id} documentId={id} hasBorderBottom={index < activeAdvisoryNeed.checkListIds.length - 1} />
                                ))}
                            </div>
                        </ZfStack>
                    )}
                    {hasEntry(activeAdvisoryNeed.knowHowIds) && (
                        <ZfStack direction="column" spacing="medium">
                            <h5>{t('titleTpKnowHow')}</h5>
                            <div>
                                {activeAdvisoryNeed.knowHowIds.map((id, index) => (
                                    <Document key={id} documentId={id} hasBorderBottom={index < activeAdvisoryNeed.knowHowIds.length - 1} />
                                ))}
                            </div>
                        </ZfStack>
                    )}
                    {hasEntry(needTools) && (
                        <ZfStack direction="column" spacing="medium">
                            <h5>{t('titleTpToolsLinks')}</h5>
                            <div>
                                {needTools.map((tool, index) =>
                                    tool ? <ToolLink key={tool.id} tool={tool} hasBorderBottom={index < needTools.length - 1} /> : null,
                                )}
                            </div>
                        </ZfStack>
                    )}
                </ZfStack>
            </div>
        </div>
    );
};
